import { html, LitElement, nothing, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { styleMap } from 'lit/directives/style-map.js'

import '@/components/icon'
import '@/components/button'
import glass from '@/assets/glass.css?inline'
import { jamCloseCircleF } from '@/icons'
import { defineFormAssociation, FormAssociationController } from '@/shared/form-association'

import style from './style.css?inline'

@customElement('web-ui-textarea')
export class WebUiTextarea extends LitElement {
  static formAssociated = true
  static override styles = [unsafeCSS(glass), unsafeCSS(style)]

  @property({ type: String, reflect: true }) placeholder = ''
  @property({ type: String, reflect: true }) name = ''
  @property({ type: Boolean, reflect: true }) disabled = false
  @property({ type: Boolean, reflect: true }) readonly = false
  @property({ type: Boolean, reflect: true }) required = false
  @property({ type: Boolean, reflect: true }) full = false
  @property({ type: Boolean, reflect: true }) clearable = false
  @property({ type: Boolean, reflect: true }) borderless = false
  @property({ type: Number, reflect: true }) rows = 3
  @property({ type: Number, reflect: true }) minlength: number | undefined
  @property({ type: Number, reflect: true }) maxlength: number | undefined
  @property({ type: Boolean, reflect: true }) autosize = false
  // 自动高度上限（px）；0 表示不限制，随内容无限增高
  @property({ type: Number, reflect: true, attribute: 'max-height' }) maxHeight = 0
  @property({ type: String, attribute: 'aria-label' }) override ariaLabel: string | null = null
  @property({ type: String, attribute: 'aria-labelledby' }) ariaLabelledby: string | undefined

  @state() private _value = ''
  @state() private _focused = false
  @state() private _hasPrefix = false
  @state() private _hasSuffix = false

  private _textarea: HTMLTextAreaElement | null = null
  private _resizeObserver: ResizeObserver | null = null

  @property({ type: String, reflect: true })
  get value(): string {
    return this._value
  }

  set value(v: string) {
    const old = this._value
    this._value = v
    this._formAssociation.sync()
    this.requestUpdate('value', old)
  }

  private get _isDisabled(): boolean {
    return this.disabled || this._formAssociation.isFormDisabled()
  }

  private readonly _formAssociation = defineFormAssociation<string>({
    host: this,
    getState: () => this._value,
    setState: value => {
      this.value = value
    },
    getFormValue: () => this._value,
    getFormState: () => this._value,
    restoreState: state => {
      if (typeof state === 'string') this.value = state
    },
    syncValidity: () => this._syncValidity()
  }).make()

  private readonly _formAssociationController = new FormAssociationController(this, this._formAssociation)

  formResetCallback() {
    this._formAssociation.reset()
  }

  formDisabledCallback(disabled: boolean) {
    this._formAssociation.setDisabled(disabled)
  }

  formStateRestoreCallback(state: string | File | FormData | null) {
    this._formAssociation.restore(state)
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    this._teardownAutosize()
  }

  override firstUpdated() {
    this._textarea = this.shadowRoot?.querySelector('textarea') ?? null
  }

  override updated(changed: Map<string, unknown>) {
    if (changed.has('_value')) {
      const textarea = this._getTextarea()
      if (textarea && textarea.value !== this._value) {
        textarea.value = this._value
      }
    }
    if (changed.has('autosize')) {
      if (this.autosize) {
        this._setupAutosize()
      } else {
        this._teardownAutosize(true)
      }
    }
    this._syncValidity()
    this.toggleAttribute('focused', this._focused)
  }

  private _getTextarea(): HTMLTextAreaElement | null {
    this._textarea ??= this.shadowRoot?.querySelector('textarea') ?? null
    return this._textarea
  }

  private _syncValidity() {
    const internals = this._formAssociation.getInternals()
    const textarea = this._getTextarea()
    if (!internals || !textarea || typeof internals.setValidity !== 'function') return

    if (this._isDisabled || textarea.validity.valid) {
      internals.setValidity({})
      return
    }

    const validity: ValidityStateFlags = {}
    if (textarea.validity.valueMissing) validity.valueMissing = true
    if (textarea.validity.tooShort) validity.tooShort = true
    if (textarea.validity.tooLong) validity.tooLong = true
    internals.setValidity(validity, textarea.validationMessage, textarea)
  }

  private _setupAutosize() {
    this._teardownAutosize()
    if (typeof ResizeObserver === 'undefined') {
      this._autosize()
      return
    }
    this._resizeObserver = new ResizeObserver(() => {
      this._autosize()
    })
    if (this._textarea) this._resizeObserver.observe(this._textarea)
    this._autosize()
  }

  private _teardownAutosize(resetHeight = false) {
    this._resizeObserver?.disconnect()
    this._resizeObserver = null
    if (resetHeight) this._textarea?.style.removeProperty('height')
  }

  private _autosize() {
    if (!this._textarea || !this.autosize) return
    this._textarea.style.height = 'auto'
    const computedMin = this.rows * 20
    const scrollH = this._textarea.scrollHeight
    // 上限由 CSS max-height（maxHeight 属性）钳制，这里只负责随内容增高，默认无限
    const height = Math.max(scrollH, computedMin)
    this._textarea.style.height = `${height}px`
  }

  private handleInput(e: Event) {
    if (!(e.target instanceof HTMLTextAreaElement)) return
    this._value = e.target.value
    this._formAssociation.sync()
    this._syncValidity()
    this._autosize()
  }

  private handleFocus() {
    if (!this._isDisabled) this._focused = true
  }

  private handleBlur() {
    this._focused = false
  }

  // 原生 change 不 composed，被 shadow root 挡住；这里补发 composed 事件，
  // 兑现 $events/README 声明的公共 change 契约。
  private handleNativeChange(e: Event) {
    if (!(e.target instanceof HTMLTextAreaElement)) return
    this._value = e.target.value
    this._formAssociation.sync()
    this._syncValidity()
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }))
  }

  override focus() {
    this.shadowRoot?.querySelector('textarea')?.focus()
  }

  override blur() {
    this.shadowRoot?.querySelector('textarea')?.blur()
  }

  select() {
    this.shadowRoot?.querySelector('textarea')?.select()
  }

  private focusTextarea() {
    if (this._isDisabled) return
    this.shadowRoot?.querySelector('textarea')?.focus()
  }

  private _onSlotChange(e: Event) {
    if (!(e.target instanceof HTMLSlotElement)) return
    const slot = e.target
    const hasContent = slot.assignedElements().length > 0
    if (slot.name === 'prefix') this._hasPrefix = hasContent
    if (slot.name === 'suffix') this._hasSuffix = hasContent
  }

  private handleClear() {
    if (this._isDisabled || this.readonly) return
    this._value = ''
    this._formAssociation.sync()
    this.dispatchEvent(new Event('input', { bubbles: true, composed: true }))
  }

  private preventMouseDownBlur(e: Event) {
    e.preventDefault()
  }

  override render() {
    // readonly 下不可清除：清空按钮会修改值，与只读语义冲突
    const showClear = this.clearable && this._value && !this.readonly

    return html`
      <div class="wui-glass wui-textarea-inner" @click=${this.focusTextarea}>
        <slot name="prefix" class=${classMap({ empty: !this._hasPrefix })} @slotchange=${this._onSlotChange}></slot>
        <textarea
          placeholder=${this.placeholder}
          name=${this.name}
          aria-label=${ifDefined(this.ariaLabel)}
          aria-labelledby=${ifDefined(this.ariaLabelledby)}
          .rows=${this.rows}
          minlength=${ifDefined(this.minlength)}
          maxlength=${ifDefined(this.maxlength)}
          ?disabled=${this._isDisabled}
          ?readonly=${this.readonly}
          ?required=${this.required}
          .value=${this._value}
          style=${this.maxHeight > 0 ? styleMap({ 'max-height': `${this.maxHeight}px` }) : nothing}
          @input=${this.handleInput}
          @change=${this.handleNativeChange}
          @focus=${this.handleFocus}
          @blur=${this.handleBlur}
        ></textarea>
        ${
          showClear
            ? html`<web-ui-button
                icon
                variant="ghost"
                size="24"
                aria-label="清除"
                @pointerdown=${this.preventMouseDownBlur}
                @click=${this.handleClear}
              >
                <web-ui-icon .icon=${jamCloseCircleF}></web-ui-icon>
              </web-ui-button>`
            : ''
        }
        <slot name="suffix" class=${classMap({ empty: !this._hasSuffix })} @slotchange=${this._onSlotChange}></slot>
      </div>
    `
  }

  declare readonly $events: {
    input: Event
    change: Event
    focus: FocusEvent
    blur: FocusEvent
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'web-ui-textarea': WebUiTextarea
  }
}
